import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import api from '../utils/api';
import AddTeacherDialog from './AddTeacherDialog';
import EditTeacherDialog from './EditTeacherDialog';
import './Teachers.css';

const Teachers = () => {
  const location = useLocation();
  const { facultyId, departmentId } = useParams();
  const [faculty, setFaculty] = useState(location.state?.faculty || null);
  const [department, setDepartment] = useState(location.state?.department || null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [draggedTeacher, setDraggedTeacher] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [dragOverTeacher, setDragOverTeacher] = useState(null);
  const [contextResolved, setContextResolved] = useState(!!(location.state?.faculty && location.state?.department));

  const fetchTeachers = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch teachers for this department using dept_ref
      const response = await api.get(`/teachers?dept_ref=${departmentId}`);
      if (response.data.success) {
        // Sort teachers by priority if available, otherwise by name
        const sortedTeachers = response.data.data.sort((a, b) => {
          if (a.priority !== undefined && b.priority !== undefined) {
            return a.priority - b.priority;
          }
          return a.name.localeCompare(b.name);
        });
        
        setTeachers(sortedTeachers);
      } else {
        setTeachers([]);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else {
        setError('Error loading teachers. Please try again.');
      }
      console.error('Error fetching teachers:', err);
    } finally {
      setLoading(false);
    }
  }, [departmentId]);

  useEffect(() => {
    const fetchContext = async () => {
      if (faculty && department) {
        return;
      }

      try {
        setLoading(true);
        setError('');

        const [facultiesResponse, departmentsResponse] = await Promise.all([
          api.get('/faculties'),
          api.get(`/departments?faculty_ref=${facultyId}`)
        ]);

        if (!facultiesResponse.data.success || !departmentsResponse.data.success) {
          throw new Error('Failed to load faculty or department');
        }

        const matchedFaculty = facultiesResponse.data.data.find(item => item._id === facultyId);
        const matchedDepartment = departmentsResponse.data.data.find(item => item._id === departmentId);

        if (!matchedFaculty || !matchedDepartment) {
          throw new Error('Faculty or department information not found');
        }

        setFaculty(matchedFaculty);
        setDepartment(matchedDepartment);
        setContextResolved(true);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Faculty or department information not found');
        setContextResolved(true);
        setLoading(false);
      }
    };

    fetchContext();
  }, [department, departmentId, faculty, facultyId]);

  useEffect(() => {
    if (faculty && department) {
      fetchTeachers();
    } else if (contextResolved) {
      setError('Faculty or department information not found');
      setLoading(false);
    }
  }, [faculty, department, fetchTeachers, contextResolved]);

  // Drag and Drop handlers
  const handleDragStart = (e, teacher) => {
    setDraggedTeacher(teacher);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, teacher) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedTeacher && draggedTeacher._id !== teacher._id) {
      setDragOverTeacher(teacher._id);
    }
  };

  const handleDrop = (e, targetTeacher) => {
    e.preventDefault();
    
    if (!draggedTeacher || draggedTeacher._id === targetTeacher._id) {
      return;
    }

    const draggedIndex = teachers.findIndex(t => t._id === draggedTeacher._id);
    const targetIndex = teachers.findIndex(t => t._id === targetTeacher._id);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    // Create new array with reordered teachers
    const newTeachers = [...teachers];
    const [removed] = newTeachers.splice(draggedIndex, 1);
    newTeachers.splice(targetIndex, 0, removed);

    // Update priorities based on new order
    const updatedTeachers = newTeachers.map((teacher, index) => ({
      ...teacher,
      priority: index + 1
    }));

    setTeachers(updatedTeachers);
    setDraggedTeacher(null);
    setDragOverTeacher(null);
    setIsReordering(true);
  };

  const handleDragEnd = () => {
    setDraggedTeacher(null);
    setDragOverTeacher(null);
  };



  // Send updated priorities to backend
  const sendUpdatedPriorities = React.useCallback(async () => {
    if (!isReordering || !department) {
      return;
    }

    try {
      const priorities = teachers.map(teacher => ({
        _id: teacher._id,
        priority: teacher.priority
      }));

      const response = await api.put('/teachers-priorities', {
        departmentId: department._id,
        priorities: priorities
      });

      if (response.data.success) {
        setIsReordering(false);
      } else {
        // Revert to original order if update fails
        fetchTeachers();
      }
    } catch (err) {
      // Revert to original order if update fails
      fetchTeachers();
    } finally {
      setIsReordering(false);
    }
  }, [teachers, isReordering, department, fetchTeachers]);

  // Auto-save priorities after reordering
  useEffect(() => {
    if (isReordering) {
      const timeoutId = setTimeout(() => {
        sendUpdatedPriorities();
      }, 1000); // Wait 1 second after last reorder

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isReordering, sendUpdatedPriorities]);


  const handleAddClick = () => {
    setIsAddDialogOpen(true);
  };

  const handleAddClose = () => {
    setIsAddDialogOpen(false);
  };

  const handleAddSave = (newTeacher) => {
    setTeachers(prevTeachers => [newTeacher, ...prevTeachers]);
  };

  const handleEditClick = (teacher) => {
    setEditingTeacher(teacher);
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
    setEditingTeacher(null);
  };

  const handleEditSave = (updatedTeacher) => {
    setTeachers(prevTeachers => {
      const updated = prevTeachers.map(teacher => 
        teacher._id === updatedTeacher._id ? updatedTeacher : teacher
      );
      
      // Re-sort to maintain proper order after edit
      return updated.sort((a, b) => {
        if (a.priority !== undefined && b.priority !== undefined) {
          return a.priority - b.priority;
        }
        return a.name.localeCompare(b.name);
      });
    });
  };

  const handleDeleteTeacher = async (teacherId) => {
    if (!window.confirm('Are you sure you want to delete this teacher? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/teachers/${teacherId}`);
      
      if (response.data.success) {
        setTeachers(prevTeachers => 
          prevTeachers.filter(teacher => teacher._id !== teacherId)
        );
      } else {
        alert('Failed to delete teacher');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        alert('Unauthorized. Please log in again.');
      } else {
        alert('Error deleting teacher. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="teachers-loading">
        <div className="loading-spinner"></div>
        <p>Loading teachers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="teachers-error">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!faculty || !department) {
    return (
      <div className="teachers-error">
        <div className="error-content">
          <h2>Information Not Found</h2>
          <p>Faculty or department information is missing.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="teachers">
      <header className="teachers-header">
        <div className="header-info">
          <div className="faculty-info">
            <h3>{faculty.name}</h3>
            <p>{faculty.nameEn}</p>
          </div>
          <div className="department-info">
            <h1>{department.name}</h1>
            <p>{department.nameEn}</p>
          </div>
        </div>
      </header>

      <main className="teachers-main">
        <div className="teachers-section">
          <div className="section-header">
            <div className="header-content">
              <div className="header-text">
                <h2>Teachers</h2>
                <p>{teachers.length} teacher{teachers.length !== 1 ? 's' : ''} found</p>

              </div>
              <button className="add-teacher-button" onClick={handleAddClick}>
                <span className="button-icon">+</span>
                Add Teacher
              </button>
            </div>







          </div>

          {teachers.length > 0 ? (
            <div className="teachers-grid">
              {teachers.map((teacher) => (
                <div
                  key={teacher._id}
                  className={`teacher-card ${draggedTeacher?._id === teacher._id ? 'dragging' : ''} ${dragOverTeacher === teacher._id ? 'drag-over' : ''}`}
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, teacher)}
                  onDragOver={(e) => handleDragOver(e, teacher)}
                  onDrop={(e) => handleDrop(e, teacher)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="teacher-header">
                    <div className="teacher-avatar">
                      {teacher.dp ? (
                        <img 
                          src={teacher.dp} 
                          alt={teacher.name} 
                          className="avatar-image"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <span 
                        className="avatar-text" 
                        style={{ display: teacher.dp ? 'none' : 'flex' }}
                      >
                        {teacher.name.charAt(0)}
                      </span>
                    </div>
                    <div className="drag-icon-container">
                      <span className="drag-icon">⋮⋮</span>
                    </div>
                  </div>
                  
                  <div className="teacher-content">
                    <div className="teacher-info">
                      <h3 className="teacher-name">{teacher.name}</h3>
                      {teacher.nameEn && (
                        <p className="teacher-name-en">{teacher.nameEn}</p>
                      )}
                      <p className="teacher-designation">{teacher.designation}</p>
                      <p className="teacher-email">{teacher.email}</p>
                      {teacher.phoneNumber && (
                        <p className="teacher-phone">{teacher.phoneNumber}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="teacher-actions">
                    <button 
                      className="edit-teacher-btn"
                      onClick={() => handleEditClick(teacher)}
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-teacher-btn"
                      onClick={() => handleDeleteTeacher(teacher._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-teachers">
              <div className="empty-state">
                <div className="empty-icon">👨‍🏫</div>
                <h3>No Teachers Yet</h3>
                <p>This department doesn't have any teachers yet.</p>
                <button className="add-teacher-btn" onClick={handleAddClick}>
                  Add First Teacher
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <AddTeacherDialog
        faculty={faculty}
        department={department}
        isOpen={isAddDialogOpen}
        onClose={handleAddClose}
        onSave={handleAddSave}
      />

      <EditTeacherDialog
        teacher={editingTeacher}
        faculty={faculty}
        department={department}
        isOpen={isEditDialogOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default Teachers;
